import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wallet as WalletIcon, Sparkles, HelpCircle, Plus, Info, Gift } from "lucide-react";
import api from "../lib/api";
import { GoldButton, SectionTitle } from "../components/common";
import { useAuth } from "../context/AuthContext";

const TYPE_LABELS = {
  purchase: "Package purchase", topup: "Top-up purchase", unlock: "Message unlock",
  offer_submission: "Date Offer fee", counter_offer: "Counteroffer fee", counter_offer_refund: "Counteroffer fee returned",
  unlock_reward: "Earned · Unlock reward", gift_refund: "Gift credits returned", redemption_reserved: "Redemption request", redemption_reversed: "Redemption reversed", gift_sent: "Gift sent", gift_received: "Earned · Gift received",
  admin_adjustment: "Admin adjustment", welcome_bonus: "Welcome credits", refund_clawback: "Refund reversal",
};

export default function Wallet() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [split, setSplit] = useState({ earned: 0, purchased: 0, promo: 0 });
  const [txns, setTxns] = useState([]);
  const [packages, setPackages] = useState([]);
  const [topups, setTopups] = useState([]);
  const [busy, setBusy] = useState(null);
  const [refundReqs, setRefundReqs] = useState([]);
  const [refundTxn, setRefundTxn] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [promo, setPromo] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);

  const load = async () => {
    const [b, cat, rr] = await Promise.all([
      api.get("/credits/balance"), api.get("/credits/catalog"), api.get("/payments/refund-requests"),
    ]);
    setBalance(b.data.credits); setTxns(b.data.transactions);
    setSplit({ earned: b.data.earned_credits || 0, purchased: b.data.purchased_credits ?? b.data.credits, promo: b.data.promotional_credits || 0 });
    setPackages(cat.data.packages || []); setTopups(cat.data.topups || []);
    setRefundReqs(rr.data.requests || []);
    refresh();
  };
  useEffect(() => { load(); }, []);

  const submitRefund = async () => {
    if (refundReason.trim().length < 5) { toast.error("Please add a brief reason (min 5 characters)."); return; }
    try {
      await api.post("/payments/refund-request", { session_id: refundTxn.session_id, reason: refundReason.trim() });
      toast.success("Refund request submitted for admin review");
      setRefundTxn(null); setRefundReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit request"); }
  };

  const buy = async (item) => {
    setBusy(item.id);
    try {
      const { data } = await api.post("/payments/checkout", { item_id: item.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); setBusy(null); }
  };

  const redeem = async () => {
    if (!promo.trim()) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post("/credits/redeem", { code: promo.trim() });
      toast.success(`Promo applied! +${data.bonus_credits} credits`, { description: `New balance: ${data.new_balance} credits.` });
      setPromo(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not redeem code"); }
    finally { setPromoBusy(false); }
  };

  const Price = ({ item }) => {
    const hasPromo = item.promo_price != null && item.promo_price < item.price;
    return (
      <div className="font-serif text-2xl text-[#D4AF37] mb-4">
        {hasPromo && <span className="text-[#9E978E] line-through text-base mr-2">${item.price}</span>}
        ${item.effective_price ?? item.price}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      <SectionTitle eyebrow="Credits Wallet" title="Your DateBid Credits" className="mb-6" />

      {/* Balance + actions */}
      <div className="db-glass rounded-3xl border border-[#D4AF37]/25 p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-[#541224]/60 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]"><WalletIcon size={26} /></div>
            <div><div className="text-[11px] uppercase tracking-[0.2em] text-[#C9A96E]">DateBid Credits</div><div data-testid="wallet-balance" className="font-serif text-4xl text-[#FBF8F3]">{balance} <span className="text-lg text-[#9E978E]">credits</span></div>
              <div className="text-xs text-[#9E978E] mt-1" data-testid="wallet-balance-split"><span className="text-[#6FD79E]" data-testid="wallet-earned-credits">{split.earned} earned</span> · <span data-testid="wallet-purchased-credits">{split.purchased} purchased</span> · <span data-testid="wallet-promo-credits">{split.promo} promotional</span> · promotional and purchased credits are used first</div></div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="wallet-earnings-link" onClick={() => navigate("/earnings")} className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-[#D4AF37]/30 text-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors">Earnings Summary</button>
            <button data-testid="wallet-how-credits-link" onClick={() => navigate("/credits/how")} className="flex items-center gap-1.5 rounded-full px-4 py-2 text-sm border border-[#D4AF37]/30 text-[#D4AF37] hover:border-[#D4AF37]/60 transition-colors"><HelpCircle size={15} /> How Credits Work</button>
          </div>
        </div>
      </div>

      {/* Offer vs Credits reminder */}
      <div className="rounded-xl bg-[#541224]/20 border border-[#D4AF37]/25 p-4 mb-8 flex items-start gap-3" data-testid="wallet-offer-vs-credits">
        <Info size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
        <p className="text-xs text-[#D8D2C9] leading-relaxed">
          <strong className="text-[#FBF8F3]">Date Offers and DateBid Credits are different.</strong> A Date Offer is a dollar amount you propose to a member. Credits are a separate platform currency used for designated features (like unlocking messages) and are never paid to another member. <button onClick={() => navigate("/credits/how")} className="text-[#D4AF37] underline underline-offset-2">Learn more</button>
        </p>
      </div>

      {/* Packages */}
      <h3 className="font-serif text-xl text-[#FBF8F3] mb-3">Credit Packages</h3>
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {packages.map((p) => (
          <div key={p.id} className={`rounded-2xl border p-6 text-center ${p.popular ? "border-[#D4AF37]/60 bg-[#541224]/30" : "border-white/10 bg-[#141113]/60"}`}>
            {p.popular ? <div className="text-[10px] uppercase tracking-wide text-[#0C0A0A] bg-[#D4AF37] rounded-full px-3 py-0.5 inline-block mb-3 font-semibold">{p.label}</div>
              : <div className="text-[11px] uppercase tracking-wide text-[#C9A96E] mb-3">{p.label}</div>}
            <div className="font-serif text-4xl text-[#FBF8F3]">{p.credits}</div>
            <div className="text-xs text-[#9E978E] mb-4">credits</div>
            <Price item={p} />
            <GoldButton data-testid={`wallet-buy-credits-btn-${p.id}`} onClick={() => buy(p)} disabled={busy === p.id} className="w-full">{busy === p.id ? "Redirecting…" : "Purchase"}</GoldButton>
          </div>
        ))}
      </div>

      {/* Top-ups */}
      {topups.length > 0 && (
        <>
          <h3 className="font-serif text-xl text-[#FBF8F3] mb-1">Quick Top-Ups</h3>
          <p className="text-xs text-[#9E978E] mb-3">Need just a few more? Add credits without buying a full package.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
            {topups.map((t) => (
              <div key={t.id} className="rounded-2xl border border-white/10 bg-[#141113]/60 p-5 text-center flex flex-col items-center">
                <div className="h-9 w-9 rounded-full bg-[#541224]/60 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] mb-2"><Plus size={16} /></div>
                <div className="font-serif text-3xl text-[#FBF8F3]">{t.credits}</div>
                <div className="text-xs text-[#9E978E] mb-3">credits</div>
                <Price item={t} />
                <GoldButton data-testid={`wallet-topup-btn-${t.id}`} onClick={() => buy(t)} disabled={busy === t.id} className="w-full text-sm py-2">{busy === t.id ? "…" : `Top Up`}</GoldButton>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="rounded-xl bg-[#0C0A0A]/40 border border-[#D4AF37]/15 p-3 text-xs text-[#9E978E] mb-8">Payments run in secure Stripe test mode. Use card 4242 4242 4242 4242, any future expiry and CVC.</div>

      {/* Promo code */}
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#141113]/60 p-5 mb-8" data-testid="promo-redeem-card">
        <h3 className="font-serif text-lg text-[#FBF8F3] mb-1 flex items-center gap-2"><Gift size={18} className="text-[#D4AF37]" /> Have a promo code?</h3>
        <p className="text-xs text-[#9E978E] mb-3">Redeem a code for bonus promotional credits.</p>
        <div className="flex gap-2">
          <input data-testid="promo-code-input" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && redeem()} placeholder="Enter code" className="flex-1 rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-4 py-2.5 text-sm text-[#FBF8F3] uppercase tracking-wide" />
          <GoldButton data-testid="promo-redeem-btn" onClick={redeem} disabled={promoBusy || !promo.trim()} className="px-6">{promoBusy ? "…" : "Redeem"}</GoldButton>
        </div>
      </div>

      {/* Ledger */}
      <h3 className="font-serif text-xl text-[#FBF8F3] mb-3">Credit History</h3>
      {txns.length === 0 ? <p className="text-sm text-[#9E978E]">No transactions yet.</p> : (
        <div className="space-y-2" data-testid="credit-ledger">
          {txns.map((t) => {
            const req = refundReqs.find((r) => r.session_id && r.session_id === t.session_id);
            const canRefund = (t.type === "purchase" || t.type === "topup") && t.session_id && !req;
            return (
              <div key={t.transaction_id} className="flex items-center justify-between rounded-xl border border-white/8 bg-[#141113]/60 px-4 py-3" data-testid={`ledger-row-${t.transaction_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-[#D8D2C9] flex items-center gap-2 flex-wrap">
                    {TYPE_LABELS[t.type] || t.type?.replace(/_/g, " ")}
                    {t.credit_source === "earned" && <span className="text-[9px] uppercase tracking-wide bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-2 py-0.5">Earned</span>}
                    {t.promotional && <span className="text-[9px] uppercase tracking-wide bg-[#2E7D52]/20 text-[#6FD79E] border border-[#2E7D52]/40 rounded-full px-2 py-0.5">Promo</span>}
                    {t.meta?.amount ? <span className="text-[#9E978E] text-xs">· ${t.meta.amount}</span> : null}
                  </div>
                  <div className="text-[11px] text-[#9E978E] mt-0.5">
                    {t.previous_balance != null && t.new_balance != null ? `Balance ${t.previous_balance} → ${t.new_balance}` : ""}
                    {t.reason ? ` · ${t.reason}` : ""}
                  </div>
                  {req && <div className="text-[11px] text-[#C9A96E] mt-0.5">Refund {req.status.replace(/_/g, " ")}</div>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {canRefund && <button data-testid={`request-refund-${t.transaction_id}`} onClick={() => setRefundTxn(t)} className="text-xs rounded-full px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37]">Request refund</button>}
                  <div className={`font-serif text-lg ${t.credits > 0 ? "text-[#6FD79E]" : "text-[#E58CA0]"}`}>{t.credits > 0 ? "+" : ""}{t.credits}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {refundTxn && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="refund-request-modal">
          <div className="w-full max-w-md rounded-2xl border border-[#D4AF37]/25 bg-[#141113] p-6">
            <h3 className="font-serif text-xl text-[#FBF8F3] mb-2">Request a Refund</h3>
            <p className="text-xs text-[#9E978E] mb-4">${refundTxn.meta?.amount} · {refundTxn.credits} credits. Your request is reviewed by an administrator — refunds are not automatic. Only still-unused credits from this purchase are reversed if approved.</p>
            <textarea data-testid="refund-reason-input" rows={3} value={refundReason} onChange={(e) => setRefundReason(e.target.value)} placeholder="Tell us why you're requesting a refund…" className="w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 px-3 py-2 text-sm text-[#FBF8F3] resize-none" />
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => { setRefundTxn(null); setRefundReason(""); }} className="text-xs rounded-full px-4 py-2 border border-white/15 text-[#9E978E]">Cancel</button>
              <button data-testid="refund-submit-btn" onClick={submitRefund} className="text-xs rounded-full px-4 py-2 border border-[#D4AF37] text-[#D4AF37]">Submit Request</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
